import { Col, Collapse, Input, Row, Spin } from "antd";
import { useEffect, useState, useCallback } from "react";
import { fetchReleasesSchedules } from "shared/service/StrmService";
import { LoadingOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import { ReactComponent as SearchIcon } from "shared/assets/images/icon-search.svg";
import { ReactComponent as QuickQuincyIcon } from "shared/assets/images/quick-quincy-start-icon.svg";
import { ReactComponent as DesertIcon } from "shared/assets/images/desert-icon.svg";
import moment from "moment";
import { useSelector } from "react-redux";
import { ArtistState } from "explorer/reducers/artistas";
import { isMobile } from "react-device-detect";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LinguagemEnum } from "shared/enums/LinguagemEnum";
import { ReleaseTypeEnum } from "shared/enums/ReleaseTypeEnum";
import { useFlags } from "launchdarkly-react-client-sdk";
import "./releases-schedule-center.scss";

const ReleasesScheduleList = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const [schedules, setSchedules] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const { artistsReleaseScheduleVariant } = useFlags();
  const { userArtist } = useSelector((state: ArtistState) => state.artists);

  const navigateToNewReleaseSchedule = () => {
    if (artistsReleaseScheduleVariant === "only-quincy") {
      history.push("/releases-schedule/new-with-quincy");
    } else {
      history.push("/releases-schedule/new");
    }
  }

  const formatDate = (date: string) =>
    moment(date).format(
      i18n.language === LinguagemEnum.Portugues ? "DD/MM/YYYY" : "MM/DD/YYYY",
    );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await fetchReleasesSchedules();
      const items = response?.response?.items ?? response?.items ?? [];
      setSchedules(items);
    } catch {
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredSchedules = schedules.filter(
    (s) => !search || s.name?.toLowerCase().includes(search?.toLowerCase()),
  );

  const LabelScheduleItem = ({ schedule }: { schedule: any }) => (
    <div className="header-item">
      <div className="coluna-nome">
        {t("pages.ReleasesSchedule.list.cronograma")} {schedule.order}
      </div>
      <div className="coluna-data">{formatDate(schedule.createdOn)}</div>
    </div>
  );

  const formatReleaseForecast = (date: string) =>
    moment(date).format("MM/YYYY");

  const getReleaseTypeLabel = (type: number) => {
    const keyByType: Record<number, string> = {
      [ReleaseTypeEnum.Single]: "pages.ReleasesSchedule.single",
      [ReleaseTypeEnum.EP]: "pages.ReleasesSchedule.ep",
      [ReleaseTypeEnum.Album]: "pages.ReleasesSchedule.album",
    };
    return t(keyByType[type] ?? "pages.ReleasesSchedule.single");
  };

  const DetailScheduleItem = ({ schedule }: { schedule: any }) => (
    <div className="schedule-detail">
      <Row className="details">
        <Col xs={24}>
          <p className="label">
            {t("pages.ReleasesSchedule.list.dataCriacao")}
          </p>
          <p className="value">{formatDate(schedule.createdOn)}</p>
        </Col>
        {schedule.aiCurrentPlan && (
          <Col xs={24}>
            <p className="label">
              {t("pages.ReleasesSchedule.list.cronograma")}
            </p>
            <p className="value schedule-name">
              {schedule.aiCurrentPlan}
            </p>
          </Col>
        )}
        {schedule.releases?.length > 0 && (
          <Col xs={24}>
            <p className="label">{t("pages.ReleasesSchedule.list.releases")}</p>
            <ul className="value releases-list">
              {schedule.releases.map((release: any, index: number) => (
                <li key={index}>
                  {release.name} -{" "}
                  {formatReleaseForecast(release.releaseForecastOn)} (
                  {getReleaseTypeLabel(release.type)})
                </li>
              ))}
            </ul>
          </Col>
        )}
      </Row>
    </div>
  );

  return (
    <div className={`releases-schedule-list ${isMobile ? "mobile" : ""}`}>
      <div
        className={`content ${isMobile ? "mobile" : ""}`}
        style={{
          background: `linear-gradient(180deg, rgba(13, 13, 13, 0.7) 0%, #0D0D0D 85.64%), url(${userArtist?.profileImage}) no-repeat top center`,
        }}
      >
        <Row>
          <p className="title-page">{t("pages.ReleasesSchedule.list.title")}</p>
        </Row>
        <Row>
          <p className="page-explain">
            {t("pages.ReleasesSchedule.list.comoFunciona")}
          </p>
        </Row>
        <Row>
          <p className="explain-text">
            {t("pages.ReleasesSchedule.list.explicacao")}
          </p>
        </Row>
        <Row className="quick-quincy">
          <div className="header">
            <QuickQuincyIcon /> Quincy
          </div>
          <div className="body">
            <p>{t("pages.ReleasesSchedule.list.quincyMessage")}</p>
          </div>
          <div className="footer">
            <button
              className="botao-gerar-quincy botao-vazado"
              onClick={navigateToNewReleaseSchedule}
            >
              <QuickQuincyIcon />{" "}
              {t("pages.ReleasesSchedule.list.criarCronograma")}
            </button>
          </div>
        </Row>
        <Row className="row-search">
          <Input
            placeholder={t("pages.ReleasesSchedule.list.pesquisar")}
            suffix={<SearchIcon />}
            onChange={(ev) => setSearch(ev.target.value)}
            disabled={loading}
          />
        </Row>
        {loading ? (
          <div className="div-loading">
            <Spin indicator={<LoadingOutlined />} />
          </div>
        ) : filteredSchedules.length === 0 ? (
          <Row>
            <Col xs={24} className="no-schedule">
              <div className="icon">
                <DesertIcon />
              </div>
              <div>
                <p className="title">
                  {t("pages.ReleasesSchedule.list.atencao")}
                </p>
                <p className="message">
                  {t("pages.ReleasesSchedule.list.nenhumCronograma")}
                </p>
              </div>
            </Col>
          </Row>
        ) : (
          <>
            <Row justify="space-between" className="row-header-collapse">
              <Col>{t("pages.ReleasesSchedule.list.cronograma")}</Col>
              <Col>{t("pages.ReleasesSchedule.list.dataCriacao")}</Col>
            </Row>
            <Row>
              <Col xs={24}>
                <Collapse
                  bordered={false}
                  defaultActiveKey={[]}
                  expandIcon={({ isActive }) =>
                    isActive ? <UpOutlined /> : <DownOutlined />
                  }
                  className="collapse-schedule"
                  items={filteredSchedules.map((schedule) => ({
                    key: schedule.id,
                    label: <LabelScheduleItem schedule={schedule} />,
                    children: <DetailScheduleItem schedule={schedule} />,
                  }))}
                  expandIconPosition="end"
                />
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default ReleasesScheduleList;
